/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Button,
  Alert,
  Pagination,
  CircularProgress,
  Paper,
  InputAdornment,
  Snackbar,
} from '@mui/material';
import { Search, FilterList, Send, Refresh } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

// Add local dependencies.
import {
  InvitationListProps,
  InvitationStatus,
  InvitationListParams,
} from '../../../types/invitation';
import { StudioRole } from '../../../types/studio';
import {
  useGetInvitationsQuery,
  useResendInvitationMutation,
  useCancelInvitationMutation,
} from '../../../features/studios/studiosApi';
import { useStudioPermissions } from '../../../hooks/useStudioPermissions';
import { getApiErrorMessage } from '../../../utils/errorHandling';
import { getRoleDisplayName } from '../../../utils/permissions';
import InvitationCard from './InvitationCard';
import InvitationStatusBadge from './InvitationStatusBadge';
import InvitationDetailDialog from './InvitationDetailDialog';

/**
 * InvitationList component displays and manages studio invitations
 * with filtering, search, and pagination capabilities.
 */
const InvitationList = ({
  studioId,
  searchParams = { studioId },
  onInvitationUpdate,
}: InvitationListProps) => {
  const { t } = useTranslation('studio');
  const [localParams, setLocalParams] = useState<InvitationListParams>({
    studioId,
    search: searchParams.search || '',
    status: searchParams.status,
    role: searchParams.role,
    page: searchParams.page || 1,
    pageSize: searchParams.pageSize || 12,
  });

  const [selectedInvitation, setSelectedInvitation] = useState<string | null>(
    null
  );
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  // Resend/cancel failure shown in a Snackbar
  const [actionError, setActionError] = useState<string | null>(null);

  // Only the studio owner may resend or cancel owner and manager invitations
  const { role: viewerRole } = useStudioPermissions();
  const canChangeInvitation = (invitationRole: StudioRole) =>
    viewerRole === StudioRole.OWNER ||
    (invitationRole !== StudioRole.OWNER &&
      invitationRole !== StudioRole.MANAGER);

  const {
    data: invitationsData,
    isLoading,
    error,
    refetch,
  } = useGetInvitationsQuery(localParams);

  const [resendInvitation, { isLoading: isResending }] =
    useResendInvitationMutation();
  const [cancelInvitation, { isLoading: isCancelling }] =
    useCancelInvitationMutation();

  const invitations = invitationsData?.results || [];
  const totalCount = invitationsData?.count || 0;
  const totalPages = Math.ceil(totalCount / (localParams.pageSize || 12));

  // Debounced search handler
  const handleSearchChange = useMemo(() => {
    let timeoutId: NodeJS.Timeout;
    return (value: string) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        setLocalParams(prev => ({
          ...prev,
          search: value,
          page: 1, // Reset to first page on search
        }));
      }, 300);
    };
  }, []);

  const handleFilterChange = (
    field: keyof InvitationListParams,
    value: any
  ) => {
    setLocalParams(prev => ({
      ...prev,
      [field]: value,
      page: 1, // Reset to first page on filter change
    }));
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setLocalParams(prev => ({ ...prev, page }));
  };

  const handleResend = async (invitationId: string) => {
    try {
      const result = await resendInvitation({
        studioId,
        invitationId,
      }).unwrap();
      onInvitationUpdate?.(result);
    } catch (err) {
      setActionError(getApiErrorMessage(err, t('invitationList.resendError')));
    }
  };

  const handleCancel = async (invitationId: string) => {
    try {
      await cancelInvitation({ studioId, invitationId }).unwrap();
      // Refetch to update the list
      refetch();
    } catch (err) {
      setActionError(getApiErrorMessage(err, t('invitationList.cancelError')));
    }
  };

  const handleView = (invitationId: string) => {
    setSelectedInvitation(invitationId);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedInvitation(null);
  };

  const selectedInvitationData = selectedInvitation
    ? invitations.find(inv => inv.id === selectedInvitation) || null
    : null;

  const clearFilters = () => {
    setLocalParams({
      studioId,
      search: '',
      status: undefined,
      role: undefined,
      page: 1,
      pageSize: 12,
    });
  };

  const hasActiveFilters =
    localParams.search || localParams.status || localParams.role;

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" onClick={() => refetch()}>
            {t('invitationList.retry')}
          </Button>
        }
      >
        {t('invitationList.error')}
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 2,
        }}
      >
        <Typography variant="h6" component="h2">
          {t('invitationList.header.title', { count: totalCount })}
        </Typography>
        <Button
          startIcon={<Refresh />}
          onClick={() => refetch()}
          disabled={isLoading}
          size="small"
        >
          {t('invitationList.header.refresh')}
        </Button>
      </Box>

      {/* Search and Filters */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Search Bar */}
          <TextField
            placeholder={t('invitationList.search.placeholder')}
            defaultValue={localParams.search}
            onChange={e => handleSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            fullWidth
            size="small"
          />

          {/* Filters Row */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <FilterList fontSize="small" color="action" />

            {/* Status Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('invitationList.filters.status')}</InputLabel>
              <Select
                value={localParams.status || ''}
                onChange={e =>
                  handleFilterChange('status', e.target.value || undefined)
                }
                label={t('invitationList.filters.status')}
              >
                <MenuItem value="">
                  {t('invitationList.filters.allStatuses')}
                </MenuItem>
                {Object.values(InvitationStatus).map(status => (
                  <MenuItem key={status} value={status}>
                    <InvitationStatusBadge
                      status={status}
                      size="small"
                      showTooltip={false}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Role Filter */}
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t('invitationList.filters.role')}</InputLabel>
              <Select
                value={localParams.role || ''}
                onChange={e =>
                  handleFilterChange('role', e.target.value || undefined)
                }
                label={t('invitationList.filters.role')}
              >
                <MenuItem value="">
                  {t('invitationList.filters.allRoles')}
                </MenuItem>
                {Object.values(StudioRole).map(role => (
                  <MenuItem key={role} value={role}>
                    {getRoleDisplayName(role)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Clear Filters */}
            {hasActiveFilters && (
              <Button size="small" onClick={clearFilters} sx={{ ml: 'auto' }}>
                {t('invitationList.filters.clearFilters')}
              </Button>
            )}
          </Box>

          {/* Active Filters Display */}
          {hasActiveFilters && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {localParams.search && (
                <Chip
                  label={t('invitationList.activeFilters.search', {
                    query: localParams.search,
                  })}
                  size="small"
                  onDelete={() => handleFilterChange('search', '')}
                />
              )}
              {localParams.status && (
                <Chip
                  label={t('invitationList.activeFilters.status', {
                    status: localParams.status,
                  })}
                  size="small"
                  onDelete={() => handleFilterChange('status', undefined)}
                />
              )}
              {localParams.role && (
                <Chip
                  label={t('invitationList.activeFilters.role', {
                    role: getRoleDisplayName(localParams.role),
                  })}
                  size="small"
                  onDelete={() => handleFilterChange('role', undefined)}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Invitations Grid */}
      {!isLoading && invitations.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(3, 1fr)',
              lg: 'repeat(4, 1fr)',
            },
            gap: 3,
          }}
        >
          {invitations.map(invitation => (
            <InvitationCard
              key={invitation.id}
              invitation={invitation}
              onResend={
                canChangeInvitation(invitation.role) ? handleResend : undefined
              }
              onCancel={
                canChangeInvitation(invitation.role) ? handleCancel : undefined
              }
              onView={handleView}
            />
          ))}
        </Box>
      )}

      {/* Empty State */}
      {!isLoading && invitations.length === 0 && (
        <Paper
          sx={{
            p: 6,
            textAlign: 'center',
            bgcolor: 'grey.50',
          }}
        >
          <Send sx={{ fontSize: 48, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {hasActiveFilters
              ? t('invitationList.empty.withFilters')
              : t('invitationList.empty.noInvitations')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hasActiveFilters
              ? t('invitationList.empty.withFiltersDescription')
              : t('invitationList.empty.noInvitationsDescription')}
          </Typography>
          {hasActiveFilters && (
            <Button onClick={clearFilters} sx={{ mt: 2 }}>
              {t('invitationList.filters.clearFilters')}
            </Button>
          )}
        </Paper>
      )}

      {/* Pagination */}
      {!isLoading && totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={localParams.page || 1}
            onChange={handlePageChange}
            color="primary"
            showFirstButton
            showLastButton
          />
        </Box>
      )}

      {/* Loading Overlay for Actions */}
      {(isResending || isCancelling) && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: 'rgba(0, 0, 0, 0.1)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* Invitation Detail Dialog */}
      <InvitationDetailDialog
        invitation={selectedInvitationData}
        open={detailDialogOpen}
        onClose={handleCloseDetailDialog}
      />

      <Snackbar
        open={!!actionError}
        autoHideDuration={6000}
        onClose={() => setActionError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setActionError(null)}
          severity="error"
          sx={{ width: '100%' }}
        >
          {actionError}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default InvitationList;
